import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
const urlBase64ToUint8Array = (base64) => {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++)
        out[i] = raw.charCodeAt(i);
    return out;
};
export const usePushNotifications = () => {
    const [state, setState] = useState({
        supported: typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window,
        permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
        subscribed: false,
    });
    // Check current subscription on mount.
    useEffect(() => {
        if (!state.supported)
            return;
        (async () => {
            try {
                const reg = await navigator.serviceWorker.ready;
                const sub = await reg.pushManager.getSubscription();
                setState((s) => ({ ...s, subscribed: !!sub }));
            }
            catch { /* ignore */ }
        })();
    }, [state.supported]);
    const subscribe = useCallback(async () => {
        if (!state.supported)
            throw new Error('Push not supported');
        const perm = await Notification.requestPermission();
        setState((s) => ({ ...s, permission: perm }));
        if (perm !== 'granted')
            throw new Error('Notification permission denied');
        // Make sure SW is registered.
        const reg = await navigator.serviceWorker.register('/sw.js');
        const { data } = await api.get('/push/public-key');
        if (!data.publicKey)
            throw new Error('Push server is not configured');
        const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            // Cast through BufferSource — TS 5.7 typing for ArrayBufferLike vs ArrayBuffer
            // is overly strict; the runtime accepts any Uint8Array.
            applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
        const json = sub.toJSON();
        await api.post('/push/subscribe', {
            endpoint: json.endpoint,
            keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        });
        setState((s) => ({ ...s, subscribed: true }));
    }, [state.supported]);
    const unsubscribe = useCallback(async () => {
        if (!state.supported)
            return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            const endpoint = sub.endpoint;
            await sub.unsubscribe();
            await api.post('/push/unsubscribe', { endpoint });
        }
        setState((s) => ({ ...s, subscribed: false }));
    }, [state.supported]);
    return { ...state, subscribe, unsubscribe };
};
